import { QueryClient } from '@tanstack/react-query'
import { qk } from 'api'
import { setGlobalAccessToken } from 'app/auth'
import { ToastContent } from 'elements/feedback/toast'
import {
  getRequestErrorMessage,
  isClientError,
  isRequestError,
} from 'lib/request'
import { useCallback, useMemo } from 'react'
import { useErrorBoundary } from 'react-error-boundary'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'

// It is a global configuration. You can change it per-query as well.
// Stale queries are refetched automatically in the background when:
// - New instances of the query mount
// - The network is reconnected.
export const useGlobalQueryClient = () => {
  const { t } = useTranslation()
  const { showBoundary } = useErrorBoundary()

  const queryClient = useMemo(() => new QueryClient(), [])

  const checkTokenValidity = useCallback(
    (error: unknown) => {
      if (
        isClientError(error) &&
        (error.code === 'error_token_expired' ||
          error.code === 'error_invalid_token' ||
          error.code === 'error_refresh_token_not_found')
      ) {
        setGlobalAccessToken(null)
        queryClient.invalidateQueries(qk.auth.token.refresh.toKey())
      }
    },
    [queryClient],
  )

  const handleQueryError = useCallback(
    (error: unknown) => {
      checkTokenValidity(error)
      if (isRequestError(error) && error.type === 'server') {
        showBoundary(new Error('SERVER_ERROR'))
      }

      const { title, message } = getRequestErrorMessage(error, t, 'query')

      toast.error(<ToastContent title={title} subtitle={message} />)
    },
    [checkTokenValidity, showBoundary, t],
  )

  const handleMutationError = useCallback(
    (error: unknown) => {
      checkTokenValidity(error)

      const { title, message } = getRequestErrorMessage(error, t, 'mutation')

      toast.error(<ToastContent title={title} subtitle={message} />)
    },
    [t, checkTokenValidity],
  )

  queryClient.setDefaultOptions({
    queries: {
      refetchOnWindowFocus: false,
      retry: false,
      onError: handleQueryError,
    },
    mutations: {
      onError: handleMutationError,
    },
  })

  return queryClient
}
